import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { Send, Bot, User, Sparkles } from "lucide-react";

const SESSION = "tutor-main";
const PRESETS = [
  "Explain RSI in simple terms",
  "How do I draw support and resistance?",
  "What is a good risk/reward ratio?",
  "Why does risk management matter so much?",
];

export default function Tutor() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const isPro = user?.plan === "pro";

  useEffect(() => {
    if (!isPro) return;
    fetch(`${API}/tutor/history/${SESSION}`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${localStorage.getItem("ta_token") || ""}` },
    }).then((r) => r.json()).then((d) => { if (Array.isArray(d)) setMessages(d); }).catch(() => {});
  }, [isPro]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/tutor/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("ta_token") || ""}` },
        body: JSON.stringify({ session_id: SESSION, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const chunk = line.slice(6);
          if (chunk === "[DONE]") continue;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the tutor. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  if (!isPro) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center glow-primary"><Bot className="w-6 h-6 text-white" /></div>
          <div>
            <h1 className="font-display text-3xl font-black tracking-tight">AI Trading Tutor</h1>
            <p className="text-slate-400 text-sm mt-0.5">Your personal trading mentor.</p>
          </div>
        </div>
        <UpgradePrompt title="The AI Tutor is a Pro feature" description="Ask unlimited questions and get instant, clear explanations. Unlock it with Pro (€3.99/month)." />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl mx-auto flex flex-col" style={{ minHeight: "calc(100vh - 8rem)" }}>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center glow-primary">
          <Bot className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">AI Trading Tutor</h1>
          <p className="text-slate-400 text-sm mt-0.5">Ask anything about trading — for learning purposes only.</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[#0F172A] border border-slate-800 rounded-2xl p-4 space-y-4" data-testid="tutor-messages">
        {messages.length === 0 && (
          <div className="h-full grid place-items-center text-center py-10">
            <div>
              <Sparkles className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <p className="text-slate-400 text-sm mb-4">Start with one of these, or ask your own question:</p>
              <div className="grid sm:grid-cols-2 gap-2 max-w-lg">
                {PRESETS.map((p) => (
                  <button key={p} data-testid={`tutor-preset-${p.slice(0, 8).toLowerCase().replace(/\s+/g, "-")}`} onClick={() => send(p)}
                    className="text-left text-sm bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 hover:border-blue-500/50 text-slate-300 transition-colors">
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${m.role === "user" ? "bg-slate-700" : "bg-blue-500/20 border border-blue-500/30"}`}>
              {m.role === "user" ? <User className="w-4 h-4 text-slate-300" /> : <Bot className="w-4 h-4 text-blue-300" />}
            </div>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-blue-600 text-white" : "bg-slate-800/70 text-slate-200"}`}>
              {m.content || (streaming && i === messages.length - 1 ? <span className="inline-block w-2 h-4 bg-blue-400 animate-pulse" /> : "")}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
        <input data-testid="tutor-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the tutor…" disabled={streaming}
          className="flex-1 bg-[#0F172A] border border-slate-700 rounded-xl px-4 py-3 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60" />
        <button type="submit" data-testid="tutor-send" disabled={streaming || !input.trim()}
          className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-xl px-5 grid place-items-center transition-colors">
          <Send className="w-4 h-4" />
        </button>
      </form>

      <DisclaimerBanner compact />
    </div>
  );
}
